import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { auth, db } from './firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import './BlogPage.css';

const BlogPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [user] = useAuthState(auth);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [reply, setReply] = useState('');
  const [showMenu, setShowMenu] = useState(null);
  const [showReplyInput, setShowReplyInput] = useState(null);

  // Fetch blog post
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const query = `
          query {
            publication(host: "hashnode.iburtum.xyz") {
              post(slug: "${id}") {
                id
                title
                content {
                  html
                }
                coverImage {
                  url
                }
                url
                publishedAt
                readTimeInMinutes
              }
            }
          }
        `;

        const response = await axios.post('https://gql.hashnode.com', { query });
        if (response.data.data.publication.post) {
          setPost(response.data.data.publication.post);
          document.title = response.data.data.publication.post.title; // Set tab title
        } else {
          setError('المقالة غير موجودة.');
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        setError('فشل تحميل المقالة. يرجى المحاولة مرة أخرى لاحقًا.');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  // Fetch comments
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'comments'), (snapshot) => {
      const commentsData = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((comment) => comment.postId === id)
        .sort((a, b) => b.timestamp - a.timestamp);

      setComments(commentsData);
    });

    return () => unsubscribe();
  }, [id]);

  // Handle comment submission
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!user || !comment.trim()) return;

    try {
      await addDoc(collection(db, 'comments'), {
        postId: id,
        userId: user.uid,
        userName: user.displayName,
        userPhoto: user.photoURL,
        text: comment,
        timestamp: Date.now(),
        replies: [],
      });
      setComment('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    }
  };

  // Handle deleting a comment
  const handleDeleteComment = async (commentId, commentUserId) => {
    if (!user || user.uid !== commentUserId) return;

    try {
      await deleteDoc(doc(db, 'comments', commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Handle deleting a reply
  const handleDeleteReply = async (commentId, replyIndex, commentUserId) => {
    if (!user || user.uid !== commentUserId) return;

    try {
      const commentRef = doc(db, 'comments', commentId);
      const commentDoc = await getDoc(commentRef);
      const updatedReplies = commentDoc.data().replies.filter((_, index) => index !== replyIndex);
      await updateDoc(commentRef, { replies: updatedReplies });
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  // Handle reply submission
  const handleReplySubmit = async (parentCommentId) => {
    if (!reply.trim()) return;

    try {
      await updateDoc(doc(db, 'comments', parentCommentId), {
        replies: arrayUnion({
          userId: user.uid,
          userName: user.displayName,
          userPhoto: user.photoURL,
          text: reply,
          timestamp: Date.now(),
        }),
      });
      setReply('');
      setShowReplyInput(null);
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-box">
          <div className="spinner"></div>
          <div className="loading-text">جار التحميل...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={() => navigate('/')}>العودة إلى المدونة</button>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="error-message">
        <p>المقالة غير موجودة.</p>
        <button onClick={() => navigate('/')}>العودة إلى المدونة</button>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <div className="blog-actions">
        <button onClick={() => navigate('/')} className="back-button">
          العودة
        </button>
        <a href={post.url} target="_blank" rel="noopener noreferrer" className="hashnode-button">
          عرض على Hashnode
        </a>
      </div>
      <h1>{post.title}</h1>
      <div className="post-meta">
        <span>تاريخ النشر: {new Date(post.publishedAt).toLocaleDateString()}</span>
        <span>وقت القراءة: {post.readTimeInMinutes} دقيقة</span>
      </div>
      {post.coverImage && (
        <img src={post.coverImage.url} alt={post.title} className="cover-image" />
      )}
      <div dangerouslySetInnerHTML={{ __html: post.content.html }} />

      <div className="comment-section">
        <h2>التعليقات</h2>
        {user ? (
          <form onSubmit={handleCommentSubmit} className="comment-form">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="أضف تعليقًا..."
              rows="2"
              required
            />
            <button type="submit">إرسال التعليق</button>
          </form>
        ) : (
          <p className="sign-in-message" class="signmes">سجل دخول للتعليق</p>
        )}

        <div className="comments-list">
          {comments.map((comment) => (
            <div key={comment.id} className="comment-box">
              <div className="comment-header">
                <button onClick={() => setShowMenu(showMenu === comment.id ? null : comment.id)} className="comment-menu-btn">⋮</button>
                {showMenu === comment.id && (
                  <div className="comment-menu">
                    <button onClick={() => setShowReplyInput(comment.id)}>رد</button>
                    {user && user.uid === comment.userId && (
                      <button onClick={() => handleDeleteComment(comment.id, comment.userId)}>حذف</button>
                    )}
                    {comment.replies && comment.replies.map((reply, index) => (
                      user && user.uid === reply.userId && (
                        <button key={index} onClick={() => handleDeleteReply(comment.id, index, reply.userId)}>
                          حذف الرد
                        </button>
                      )
                    ))}
                  </div>
                )}
              </div>
              <div className="comment-content">
                <img src={comment.userPhoto} alt={comment.userName} className="comment-avatar" />
                <div className="comment-text">
                  <p className="comment-author">{comment.userName}</p>
                  <p>{comment.text}</p>
                </div>
              </div>
              {showReplyInput === comment.id && (
                <div className="reply-input">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="أضف ردًا..."
                    rows="2"
                  />
                  <button onClick={() => handleReplySubmit(comment.id)}>إرسال الرد</button>
                </div>
              )}

              {/* Display Replies */}
              {comment.replies && comment.replies.length > 0 && (
                <div className="replies-section">
                  {comment.replies.map((reply, index) => (
                    <div key={index} className="reply-box">
                      <div className="reply-content">
                        <img src={reply.userPhoto} alt={reply.userName} className="reply-avatar" />
                        <div className="reply-text">
                          <p className="reply-author">{reply.userName}</p>
                          <p>{reply.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;